import React, { useEffect, useState, useRef } from "react";
import DOMPurify from 'dompurify';
import ChatBot from "react-simple-chatbot";
import { ThemeProvider } from "styled-components";
import axios from 'axios';
import { setupInterceptorsTo } from "../Componentes/API/AxiosBuilder";
import {
    ACCESO_MODIFICAR_CHATBOT,
    ACCESO_ELIMINAR_CHATBOT,
    ACCESO_CREAR_CHATBOT,
    URL_API_SIA
} from "../Contantes/Constantes";
import SinapThemev2 from "../styles/SinapThemev2";
import CloseIcon from '@mui/icons-material/Close';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Dialog from '@mui/material/Dialog';
import ModalFormularioIncidencia from './ModalFormularioIncidencia';
import ModalIncidencias from './ModalIncidencias';
import { useAuth } from "../Auth/State/AuthContext";
import KeyCloak from "../Auth/Keycloak";
import { Box, Typography, Button } from "@mui/material";


// Remplaza URLs por <a> y \n por <br>
const autoLinkAndBreaks = (s) => {
    const urlRegex = /(\bhttps?:\/\/[^\s<>"]+|\bwww\.[^\s<>"]+|\bmailto:[^\s<>"]+)/gi;
    return String(s ?? "")
        .replace(urlRegex, (m) => {
            const href = m.startsWith("www.") ? `https://${m}` : m;
            // color:inherit para respetar el color de la burbuja del bot
            return `<a href="${href}" target="_blank" rel="noopener noreferrer" style="color:white; text-decoration:underline;">${m}</a>`;
        })
        .replace(/\n/g, "<br>");
};

const hasHtmlTags = (s) => /<\/?[a-z][\s\S]*>/i.test(String(s ?? ""));

const SafeHTML = ({ text }) => {
    const str = String(text ?? "");
    const html = hasHtmlTags(str) ? str : autoLinkAndBreaks(str);
    return <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html) }} />;
};

const stripHtml = (s) => String(s ?? "").replace(/<\/?[^>]+(>|$)/g, "").trim();

/* ============================================ */

const ChatBotIncidencias = ({ isAuthenticated, onClose }) => {
    const [steps, setSteps] = useState([]);
    const axiosInstance = setupInterceptorsTo(axios.create());
    const [incidencias, setIncidencias] = useState([]);
    const [anchorEl, setAnchorEl] = useState(null);
    const [openFormulario, setOpenFormulario] = useState(false);
    const [openListado, setOpenListado] = useState(false);
    const { hasRole } = useAuth();
    const [rolCrear, setRolCrear] = useState(false);
    const [rolEditar, setRolEditar] = useState(false);
    const [rolEliminar, setRolEliminar] = useState(false);
    const [confirmarCerrar, setConfirmarCerrar] = useState(false);

    // Ref al root del ChatBot para manejar autoscroll
    const chatRootRef = useRef(null);

    const cerrarChatbot = () => {
        setConfirmarCerrar(false);
        requestAnimationFrame(() => {
            onClose();
        });
    };

    const handleMenuClick = (event) => setAnchorEl(event.currentTarget);
    const handleMenuClose = () => setAnchorEl(null);
    const handleAbrirFormulario = () => { handleMenuClose(); setOpenFormulario(true); };
    const handleAbrirListado = () => { handleMenuClose(); setOpenListado(true); };
    const handleCerrarFormulario = () => setOpenFormulario(false);
    const handleCerrarListado = () => setOpenListado(false);

    // Estilos globales: eliminar footer y cualquier espacio residual, forzar layout a 100% y autoscroll en el área de contenido
    useEffect(() => {
        const style = document.createElement("style");
        style.setAttribute("data-chatbot-fix", "true");
        style.innerHTML = `
          /* Estirar contenedor y eliminar márgenes */
          .rsc-container { 
            height: 100% !important; 
            display: flex !important; 
            flex-direction: column !important; 
            margin: 0 !important;
          }
          /* Área de mensajes ocupa todo y scrollea */
          .rsc-content { 
            flex: 1 1 auto !important; 
            overflow-y: auto !important;
            padding-bottom: 0 !important;
          }
          /* Footer oculto y sin alto/borde -> evita la "línea" o hueco */
          .rsc-footer, .rsc-input, .rsc-submit-button, .rsc-controls { 
            display: none !important; 
            height: 0 !important; 
            padding: 0 !important; 
            margin: 0 !important; 
            border: 0 !important;
          }
          /* Links dentro de burbujas del bot */
          .rsc-ts-bubble a {
            color: white !important;
            text-decoration: underline !important;
          }
          .rsc-ts-bubble a:hover {
            color: #e0e0e0 !important;
          }
          /* Quitar cualquier sombra/borde que genere la línea inferior */
          .rsc-container,
          .rsc-content,
          .rsc-header,
          .rsc-footer,
          .rsc-quick-buttons {
            box-shadow: none !important;
            border: 0 !important;
            filter: none !important;
          }

          /* Evitar "fade" o pseudo-elementos que parezcan una línea */
          .rsc-content::after,
          .rsc-content::before {
            content: none !important;
            display: none !important;
          }

          /* Cero márgenes/padding que dejen hueco debajo */
          .rsc-container,
          .rsc-content,
          .rsc-quick-buttons {
            margin: 0 !important;
            padding-bottom: 0 !important;
          }

          /* Si aparecen quick buttons, que no proyecten sombra */
          .rsc-quick-buttons button {
            box-shadow: none !important;
          }
          /* Fix: alinear mensajes consecutivos del bot */
           .rsc-ts-bot + .rsc-ts-bot .rsc-ts-bubble {
            margin-left: 40px !important;
          }
          /* Burbujas más anchas */
           .rsc-ts-bubble {
            max-width: 75% !important;
            }
        `;
        document.head.appendChild(style);
        return () => {
            style.remove();
        };
    }, []);

    // Autoscroll: observa cambios en el contenido y baja al final
    useEffect(() => {
        const root = chatRootRef.current;
        if (!root) return;
        const content = root.querySelector('.rsc-content');
        if (!content) return;

        const scrollToBottom = () => {
            // forzar al final; requestAnimationFrame para esperar layout
            requestAnimationFrame(() => {
                content.scrollTop = content.scrollHeight;
            });
        };

        // scroll inicial
        scrollToBottom();

        const observer = new MutationObserver(scrollToBottom);
        observer.observe(content, { childList: true, subtree: true });

        window.addEventListener('resize', scrollToBottom);
        return () => {
            observer.disconnect();
            window.removeEventListener('resize', scrollToBottom);
        };
    }, [steps]); // cuando cambia la definición de steps, reatacha

    useEffect(() => {
        VerificarRoles();
    }, [isAuthenticated]);

    useEffect(() => {
        cargarIncidencias();
    }, []);

    const VerificarRoles = () => {
        if (isAuthenticated) {
            setRolCrear(hasRole(ACCESO_CREAR_CHATBOT));
            setRolEditar(hasRole(ACCESO_MODIFICAR_CHATBOT));
            setRolEliminar(hasRole(ACCESO_ELIMINAR_CHATBOT));
        } else {
            setRolCrear(false);
            setRolEditar(false);
            setRolEliminar(false);
        }
    };

    const cargarIncidencias = async () => {
        try {
            const res = await fetch(`${URL_API_SIA}/chatbot/incidencias`);
            if (!res.ok) {
                throw new Error("Error en la respuesta del servidor");
            }
            const data = await res.json();

            if (!data || data.length === 0) {
                setSteps([
                    {
                        id: 'error',
                        component: <SafeHTML text="Ocurrió un error, por favor intente de nuevo en unos minutos" />,
                        asMessage: true,
                        end: true
                    }
                ]);
                return;
            }

            setIncidencias(data);
            const allSteps = generarPasos(data);
            setSteps(allSteps);

        } catch (error) {
            console.error("Error al cargar incidencias:", error);
            setSteps([
                {
                    id: 'error',
                    component: <SafeHTML text="Ocurrió un error, por favor intente de nuevo en unos minutos" />,
                    asMessage: true,
                    end: true
                }
            ]);
        }
    };

    const generarPasos = (incidencias) => {
        const mapa = new Map();
        incidencias.forEach(i => mapa.set(i.id, { ...i, hijos: [] }));

        incidencias.forEach(i => {
            if (i.id_padre !== null) {
                const padre = mapa.get(i.id_padre);
                if (padre) {
                    padre.hijos.push(i);
                } else {
                    console.warn(`Incidencia con id_padre=${i.id_padre} no encontrada.`);
                }
            }
        });

        const mensajesAleatorios = [
            "Gracias por tu respuesta.",
            "Perfecto, continuamos.",
            "Entendido, sigamos.",
            "Gracias, eso me ayuda.",
            "Recibido, veamos lo siguiente."
        ];

        const mensajeAleatorio = () => {
            const i = Math.floor(Math.random() * mensajesAleatorios.length);
            return mensajesAleatorios[i];
        };

        const steps = [
            {
                id: 'mensaje-bienvenida',
                component: <SafeHTML text="¡Buenas! Soy Uti" />,
                asMessage: true,
                trigger: 'inicio'
            },
            {
                id: 'inicio',
                component: <SafeHTML text="¿En qué te puedo ayudar?" />,
                asMessage: true,
                trigger: 'opciones-root'
            }
        ];

        const opcionesRaiz = Array.from(mapa.values()).filter(i => i.id_padre === null);

        steps.push({
            id: 'opciones-root',
            options: opcionesRaiz.map(op => ({
                value: op.id,
                label: stripHtml(op.contenido),
                trigger: () => `inc-${op.id}`
            }))
        });

        const agregarPaso = (incidencia) => {
            const idPaso = `inc-${incidencia.id}`;
            const hijos = incidencia.hijos;

            if (hijos.length === 0) return;

            // caso donde todos los hijos son finales
            const todosLosHijosSonFinales = hijos.every(hijo => {
                const hijoCompleto = mapa.get(hijo.id);
                return !hijoCompleto.hijos || hijoCompleto.hijos.length === 0;
            });

            if (todosLosHijosSonFinales) {
                // mostramos el contenido de la incidencia actual
                steps.push({
                    id: idPaso,
                    component: <SafeHTML text={incidencia.contenido} />,
                    asMessage: true,
                    // antes iba a 'puntuacion', ahora va a 'acciones-finales'
                    trigger: `mensaje-final-${hijos[0].id}`
                });

                // agregamos cada mensaje final
                hijos.forEach((hijo, idx) => {
                    const siguiente = (idx < hijos.length - 1)
                        ? `mensaje-final-${hijos[idx + 1].id}`
                        : 'acciones-finales'; // <-- acá cambiamos el último para que vaya a las acciones
                    steps.push({
                        id: `mensaje-final-${hijo.id}`,
                        component: <SafeHTML text={hijo.contenido} />,
                        asMessage: true,
                        trigger: siguiente
                    });
                });
                return;
            }

            // caso con hijos mixtos
            steps.push({
                id: idPaso,
                component: <SafeHTML text={incidencia.contenido} />,
                asMessage: true,
                trigger: `${idPaso}-opciones`
            });

            steps.push({
                id: `${idPaso}-opciones`,
                options: [
                    ...hijos.map(h => {
                        const hijoCompleto = mapa.get(h.id);
                        const tieneNietos = hijoCompleto.hijos && hijoCompleto.hijos.length > 0;

                        if (!tieneNietos) {
                            const idFinal = `mensaje-final-${h.id}`;
                            if (!steps.find(s => s.id === idFinal)) {
                                steps.push({
                                    id: idFinal,
                                    component: <SafeHTML text={hijoCompleto.contenido} />,
                                    asMessage: true,
                                    // antes era 'puntuacion', ahora acciones finales
                                    trigger: 'acciones-finales'
                                });
                            }

                            return {
                                value: h.id,
                                label: stripHtml(h.contenido),
                                trigger: idFinal
                            };
                        }

                        const idIntermedio = `agradecimiento-${h.id}`;
                        if (!steps.find(s => s.id === idIntermedio)) {
                            steps.push({
                                id: idIntermedio,
                                component: <SafeHTML text={mensajeAleatorio()} />,
                                asMessage: true,
                                trigger: `inc-${h.id}`
                            });
                        }
                        return {
                            value: h.id,
                            label: stripHtml(h.contenido),
                            trigger: idIntermedio
                        };

                    }),
                    {
                        value: 'volver',
                        label: '🔁 Volver al inicio',
                        trigger: 'inicio'
                    }
                ]
            });

            // recursivo
            hijos.forEach(hijo => {
                const hijoCompleto = mapa.get(hijo.id);
                if (hijoCompleto.hijos && hijoCompleto.hijos.length > 0) {
                    agregarPaso(hijoCompleto);
                }
            });
        };

        // construimos el árbol
        opcionesRaiz.forEach(op => agregarPaso(mapa.get(op.id)));

        // NUEVO PASO: acciones finales (Reiniciar / Calificar)
        steps.push({
            id: 'acciones-finales',
            options: [
                {
                    value: 'reiniciar',
                    label: '🔁 Volver al inicio',
                    trigger: 'inicio'
                },
                {
                    value: 'calificar',
                    label: '⭐ Calificar',
                    trigger: 'puntuacion'
                }
            ]
        });

        // flujo de calificación (solo cuando el usuario lo pide)
        steps.push({
            id: 'puntuacion',
            component: <SafeHTML text="¿Cómo calificarías la atención? La encuesta es confidencial y anónima." />,
            asMessage: true,
            trigger: 'aclaracion'
        });
        steps.push({
            id: 'aclaracion',
            component: <SafeHTML text="Luego de calificar podrás hacer una nueva consulta." />,
            asMessage: true,
            trigger: 'estrellas'
        });

        steps.push({
            id: 'estrellas',
            component: <SelectorEstrellas />,
            waitAction: true,
            asMessage: true
        });

        steps.push({
            id: 'fin',
            component: <SafeHTML text="¡Gracias por tu feedback!" />,
            asMessage: true,
            trigger: 'reinicio'
        });

        steps.push({
            id: 'reinicio',
            options: [
                {
                    value: 'reiniciar',
                    label: '🔁 Volver al inicio',
                    trigger: 'inicio'
                }
            ]
        });

        return steps;
    };

    const SelectorEstrellas = ({ triggerNextStep }) => {
        const [hovered, setHovered] = useState(0);
        const [selected, setSelected] = useState(0);
        const [disabled, setDisabled] = useState(false);

        const handleClick = (valor) => {
            if (disabled) return;
            setSelected(valor);
            setDisabled(true);
            enviarFeedback(valor);
            triggerNextStep({ value: valor, trigger: 'fin' });
        };

        const handleMouseEnter = (n) => { if (!disabled) setHovered(n); };
        const handleMouseLeave = () => { if (!disabled) setHovered(0); };

        return (
            <div>
                {[1, 2, 3, 4, 5].map((n) => (
                    <span
                        key={n}
                        onMouseEnter={() => handleMouseEnter(n)}
                        onMouseLeave={handleMouseLeave}
                        onClick={() => handleClick(n)}
                        style={{
                            cursor: disabled ? 'default' : 'pointer',
                            fontSize: '2rem',
                            marginRight: 3,
                            transition: 'color 0.2s',
                            color: n <= (hovered || selected) ? '#FFD700' : '#CCC'
                        }}
                    >
                        ★
                    </span>
                ))}
            </div>
        );
    };

    const enviarFeedback = async (estrellas) => {
        try {
            await fetch(`${URL_API_SIA}/chatbot/encuesta`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ calificacion: estrellas }),
            });
        } catch (error) {
            console.error("Error al enviar la puntuación:", error);
        }
    };

    const theme = {
        background: '#ffffff',
        fontFamily: SinapThemev2.typography.fontFamily,
        headerBgColor: SinapThemev2.palette.primary.main,
        headerFontColor: SinapThemev2.palette.primary.contrastText,
        headerFontSize: '16px',
        botBubbleColor: SinapThemev2.palette.primary.main,
        botFontColor: SinapThemev2.palette.primary.contrastText,
        userBubbleColor: '#ffffff',
        userFontColor: '#4a4a4a',
    };

    return (
        <div style={{ maxWidth: "100%", height: "100%", display: "flex", flexDirection: "column", backgroundColor: "#fff", position: 'relative' }}>
            <div style={{
                backgroundColor: theme.headerBgColor,
                color: theme.headerFontColor,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '8px 12px'
            }}>
                <div style={{ fontWeight: 'bold' }}>Chat</div>
                <div>
                    {(rolCrear || rolEditar || rolEliminar) && (
                        <IconButton size="small" onClick={handleMenuClick}>
                            <MoreVertIcon style={{ color: theme.headerFontColor }} />
                        </IconButton>
                    )}
                    <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
                        {rolCrear && (
                            <MenuItem onClick={handleAbrirFormulario}>Agregar nueva incidencia</MenuItem>
                        )}
                        {(rolEditar || rolEliminar) && (
                            <MenuItem onClick={handleAbrirListado}>Ver incidencias</MenuItem>
                        )}
                    </Menu>
                    <IconButton size="small" onClick={() => setConfirmarCerrar(true)}>
                        <CloseIcon style={{ color: theme.headerFontColor }} />
                    </IconButton>
                </div>
            </div>

            <div style={{ flex: 1, overflow: "hidden" /* oculto overflow aquí; lo maneja .rsc-content */ }}>
                {steps.length > 0 && (
                    <ThemeProvider theme={theme}>
                        <div ref={chatRootRef} style={{ height: "100%" }}>
                            <ChatBot
                                steps={steps}
                                hideHeader={true}
                                customStyle={{ height: "100%" }}
                                customDelay={0}
                            />
                        </div>
                    </ThemeProvider>
                )}
            </div>

            <Dialog open={openFormulario} onClose={handleCerrarFormulario} fullWidth maxWidth="md">
                <ModalFormularioIncidencia
                    open={openFormulario}
                    onClose={handleCerrarFormulario}
                    incidenciasPadre={incidencias}
                />
            </Dialog>

            <Dialog open={openListado} onClose={handleCerrarListado} fullWidth maxWidth="md">
                <ModalIncidencias
                    open={openListado}
                    onClose={handleCerrarListado}
                    incidenciasList={incidencias}
                    refreshIncidencias={cargarIncidencias}
                />
            </Dialog>

            {confirmarCerrar && (
                <>
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            backgroundColor: 'rgba(0,0,0,0.5)',
                            zIndex: 9998,
                        }}
                        onClick={() => setConfirmarCerrar(false)}
                    />

                    <Box
                        sx={{
                            position: 'absolute',
                            top: '20%',
                            left: '5%',
                            right: '5%',
                            backgroundColor: '#fff',
                            border: `1px solid ${SinapThemev2.palette.divider}`,
                            borderRadius: 2,
                            boxShadow: 5,
                            zIndex: 9999,
                            p: 2,
                        }}
                    >
                        <Typography variant="h6" gutterBottom>
                            ¿Querés cerrar la conversación?
                        </Typography>
                        <Typography variant="body2" gutterBottom>
                            Si cerrás, se va a perder la conversación que tuvimos. Podés volver a chatear conmigo cuando quieras.
                        </Typography>
                        <Box mt={2} display="flex" justifyContent="flex-end" gap={1}>
                            <Button onClick={() => setConfirmarCerrar(false)}>
                                Cancelar
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={cerrarChatbot}
                            >
                                Cerrar
                            </Button>
                        </Box>
                    </Box>
                </>
            )}
        </div>
    );
};

export default ChatBotIncidencias;
